import { useEffect, useState } from 'react';
import cx from 'classnames';
import { Heading, Pill, enums, Icon, utils as u, Loader } from 'edrolo-ui';
import { useAnalytics } from 'edrolo/analytics';
import { Attachment } from '../../../../../../types/Assemblage';
import { Link } from 'react-router-dom';
import s from './styles.scss';
import { useWatermarkedCourseAttachment } from 'edrolo/domains/attachments/hooks';
import { getPillSpecs } from './utils';
import { startCase } from 'lodash';
import { ATTACHMENT_MIME_TYPES_TO_LABELS } from '../../constants';

interface Props {
  attachment: Attachment;
  courseId: string | number;
  classId: string | number;
}

export const AttachmentCard = ({ attachment, courseId, classId }: Props) => {
  // Toggle the display of a loading spinner for watermarked attachments
  // Distinct from isUrlRequested, because we don't always want to show the loading state to the user
  const [showLoading, setShowLoading] = useState(false);

  const {
    url: watermarkedUrl,
    setIsUrlRequested,
    error,
  } = useWatermarkedCourseAttachment(classId, courseId, attachment.id);

  const { track: analyticsTrack } = useAnalytics({});
  const track = (eventName: string) =>
    analyticsTrack(eventName, {
      origin: 'attachment card',
      // TODO these should be added to an enrichment endpoint
      attachment_id: attachment.id,
      attachment_title: attachment.title,
      attachment_url: attachment.url,
      attachment_category: attachment.category,
      attachment_type: attachment.attachment_type,
      attachment_mime_type: attachment.mime_type,
    });

  const awaitingWatermark: boolean =
    attachment.requires_watermark && !watermarkedUrl;

  useEffect(() => {
    setIsUrlRequested(awaitingWatermark);

    if (attachment.requires_watermark && watermarkedUrl) {
      setShowLoading(false);
    }

    if (error) {
      setShowLoading(false);
    }

    return () => {
      setIsUrlRequested(false);
    };
  }, [awaitingWatermark, error]); // eslint-disable-line react-hooks/exhaustive-deps

  const attachmentFormatLabel =
    ATTACHMENT_MIME_TYPES_TO_LABELS[
      attachment.mime_type as keyof typeof ATTACHMENT_MIME_TYPES_TO_LABELS
    ];

  const { iconName, pillColor } = getPillSpecs(attachment.category);

  const pillText =
    attachment.attachment_type === 'link'
      ? 'EXTERNAL LINK'
      : attachmentFormatLabel;

  return (
    <div
      onMouseEnter={() => setShowLoading(awaitingWatermark)}
      className={cx(s.attachmentCard, {
        [s.loading]: showLoading,
      })}
      data-appcues="attachment-card"
    >
      <div className={s.titleBlock} data-test="title-block">
        <Link
          to={{
            pathname: attachment.requires_watermark
              ? watermarkedUrl
              : attachment.url,
          }}
          target="_blank"
          onClick={() => {
            track('attachment clicked');
          }}
        >
          <Pill variant={enums.PillVariant.FADE} color={pillColor}>
            <Icon name={iconName} className={u.mr1} />
            {startCase(attachment.category) || 'Attachment'}
          </Pill>
        </Link>

        <Link
          to={{
            pathname: attachment.requires_watermark
              ? watermarkedUrl
              : attachment.url,
          }}
          target="_blank"
          onClick={() => {
            track('attachment clicked');
          }}
          data-test="attachment-link"
        >
          <Heading level={4} className={u.bold}>
            {attachment.title}
          </Heading>
        </Link>

        {pillText && (
          <Link
            to={{
              pathname: attachment.requires_watermark
                ? watermarkedUrl
                : attachment.url,
            }}
            target="_blank"
            onClick={() => {
              track('attachment clicked');
            }}
          >
            <Pill
              variant={enums.PillVariant.OUTLINE}
              color={enums.ColorTheme.FOREGROUND}
            >
              {pillText}
            </Pill>
          </Link>
        )}

        {showLoading && !error && (
          <div className={cx(u.flex, u.gap2)}>
            <Loader.Spinner />
            <span className={u.fontSize1}>
              Loading attachment... this may take a minute.
            </span>
          </div>
        )}

        {attachment.requires_watermark && error && (
          <>
            <Icon
              name={enums.Icons.RISK_ASSESS}
              color={enums.ColorHue.RED}
              className={u.mr1}
            />
            <span className={cx(u.fontSize1, s.errorText)}>
              An Error has occurred. Please try again later.
            </span>
          </>
        )}
      </div>

      {attachment.description && (
        <div className={cx(u.fontSize1, u.mt4)}>{attachment.description}</div>
      )}
    </div>
  );
};
